import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Chip, useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, List, ListItem, ListItemIcon, ListItemText, alpha } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { School, Groups, EmojiEvents, Public, Phone, Email, LocationOn, AccessTime, CheckCircle, Flight, AccountBalance, Verified } from '@mui/icons-material';
import HeroSection from '../components/home/HeroSection';
import FeatureHighlights from '../components/home/FeatureHighlights';
import AdmissionProcess from '../components/home/AdmissionProcess';
import CollegeShowcase from '../components/home/CollegeShowcase';
import FMGEStatistics from '../components/home/FMGEStatistics';
import AchievementGrid from '../components/home/AchievementGrid';
import ServicesList from '../components/home/ServicesList';
import TestimonialCarousel from '../components/common/TestimonialCarousel';
import BlogSection from '../components/home/BlogSection';
import PortfolioSection from '../components/home/PortfolioSection';
import CountriesSection from '../components/home/CountriesSection';
import FAQSection from '../components/home/FAQSection';
import EnquiryForm from '../components/common/EnquiryForm';
import { CONTACT_CALL_DISPLAY, CONTACT_EMAIL } from '../utils/contact';


// ---------- About Section ----------
const aboutStats = [
  { icon: <School />, number: '15+', label: 'Years Experience' },
  { icon: <Groups />, number: '5000+', label: 'Students Placed' },
  { icon: <EmojiEvents />, number: '98%', label: 'Success Rate' },
  { icon: <Public />, number: '15+', label: 'Countries' },
];

const AboutSection = () => (
  <Box id="about" sx={{ scrollMarginTop: 100 }}>
    <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
      <Container maxWidth="xl">
        <Typography variant="h2" align="center" sx={{ mb: 4 }}>
          About <Box component="span" sx={{ color: '#D4A853' }}>Learn Rabbit</Box>
        </Typography>
        <Typography variant="h6" align="center" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
          Your trusted partner for medical education consultancy with 15+ years of excellence
        </Typography>
      </Container>
    </Box>
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
          Our Mission
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: 'text.secondary', textAlign: 'center', fontSize: '1.1rem' }}>
          At Learn Rabbit Pvt. Ltd., we are committed to guiding aspiring medical students towards 
          their dream careers. Our mission is to provide transparent, reliable, and comprehensive 
          admission guidance for MBBS, MD, and MS programs in India and abroad.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', textAlign: 'center', fontSize: '1.1rem' }}>
          With over 15 years of experience in medical education consultancy, we have successfully 
          placed 5000+ students in top medical colleges across 15+ countries. Our team of expert 
          counselors provides personalized guidance at every step of the admission process.
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ mt: 8 }}>
        {aboutStats.map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ color: 'secondary.main', mb: 2, fontSize: 40 }}>{stat.icon}</Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stat.number}
              </Typography>
              <Typography variant="h6" color="text.secondary">{stat.label}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ---------- Contact Section ----------
const contactInfo = [
  {
    icon: <Phone sx={{ fontSize: 40 }} />,
    title: 'Phone',
    details: [CONTACT_CALL_DISPLAY],
  },
  {
    icon: <Email sx={{ fontSize: 40 }} />,
    title: 'Email',
    details: [CONTACT_EMAIL],
  },
  {
    icon: <LocationOn sx={{ fontSize: 40 }} />,
    title: 'Address',
    details: ['123 Education Street,', 'New Delhi - 110001, India'],
  },
  {
    icon: <AccessTime sx={{ fontSize: 40 }} />,
    title: 'Working Hours',
    details: ['Mon - Sat: 9:00 AM - 7:00 PM', 'Sunday: 10:00 AM - 4:00 PM'],
  },
];

const ContactSection = () => (
  <Box id="contact" sx={{ scrollMarginTop: 100 }}>
    <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
      <Container maxWidth="xl">
        <Typography variant="h2" align="center" sx={{ mb: 4 }}>
          Contact <Box component="span" sx={{ color: '#D4A853' }}>Us</Box>
        </Typography>
        <Typography variant="h6" align="center" sx={{ opacity: 0.9 }}>
          Get in touch with our expert counselors for free guidance
        </Typography>
      </Container>
    </Box>
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={7}>
          <Grid container spacing={3}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{ color: 'secondary.main', mb: 2 }}>{info.icon}</Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {info.title}
                    </Typography>
                    {info.details.map((detail, idx) => (
                      <Typography key={idx} variant="body1" color="text.secondary">
                        {detail}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 6, borderRadius: 2, overflow: 'hidden' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.123456789!2d77.123456!3d28.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDA3JzI0LjQiTiA3N8KwMDcnMjQuNCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Office Location"
            ></iframe>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <EnquiryForm />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// ---------- Main SectionsPage ----------
const SectionsPage = () => {
  const location = useLocation();

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, []);

  // Expose scrollToSection globally for Header component
  useEffect(() => {
    window.__scrollToSection = scrollToSection;
    return () => {
      delete window.__scrollToSection;
    };
  }, [scrollToSection]);

  // Scroll to a section when navigating from another page via route state
  useEffect(() => {
    if (location.state?.scrollTo) {
      // Use a small delay to ensure the target section DOM has rendered
      const timer = setTimeout(() => {
        scrollToSection(location.state.scrollTo);
        // Clear the state so it doesn't re-scroll on re-renders
        window.history.replaceState({}, document.title);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.state, scrollToSection]);

  return (
    <>
      <Helmet>
        <title>Learn Rabbit - Medical Admissions Portal | MBBS, MD, MS</title>
        <meta
          name="description"
          content="Get expert guidance for MBBS, MD, MS admissions in India and abroad. Top medical colleges, NEET counselling, FMGE preparation. 5000+ students placed."
        />
      </Helmet>

      <Box component="main">
        {/* Home Section */}
        <Box id="home" sx={{ scrollMarginTop: 100 }}>
          <HeroSection />
          <FeatureHighlights />
          <AdmissionProcess />
          <CollegeShowcase />
          <FMGEStatistics />
          <AchievementGrid />
          <ServicesList />
          <TestimonialCarousel />
          <BlogSection />
          <FAQSection />
        </Box>

        {/* Countries Section */}
        <CountriesSection />

        {/* Portfolio Section */}
        <PortfolioSection />

        {/* About Section */}
        <AboutSection />

        {/* Contact Section */}
        <ContactSection />

      </Box>
    </>
  );
};

export default SectionsPage;